<!-- Generic format_specific_data form: one heading per x-ui group, fields in x-ui order, a nested
     object as a fieldset of its own fields. Nothing here names a template or a key; everything
     comes from the schema (see fsd-schema.ts). An empty schema renders nothing. -->
@if (!empty) {
  @if (ruleBefore) {
    <hr class="cl-spacing-medium">
  }
  <!-- Keyed by the schema revision: a new template re-creates the groups. A block is one group, or a
       `sections` heading (section-headings.ts) over its member groups; rulers only between
       blocks. Groups are div role="group", not <section>: castlabs-ui pads every section, which
       stacked into large gaps under the rulers. -->
  @for (rev of [schemaRev]; track rev) {
  @for (block of blocks; track block.key) {
    @if (block.ruled) {
      <hr class="cl-spacing-medium">
    }
    @if (block.title) {
      <div role="group" class="cl-spacing-medium" [attr.data-section]="block.title" [attr.aria-labelledby]="idPrefix + '-' + block.key">
        <h3 [id]="idPrefix + '-' + block.key">{{ block.title }}</h3>
        <!-- One column per member group: its lead control on top, its details right under it. -->
        <div class="row">
          @for (group of block.columns; track group.name) {
            @if (toggles.get(group.name); as t) {
              <!-- An on/off lead (Watermark): a checkbox with its description, the group's other fields
                   beside it, so it needs the full width. -->
              <div class="col-12" [attr.data-group]="group.name">
                <div [attr.data-field]="t.field.path">
                  <app-dl-checkbox [checkboxId]="fieldId(t.field)" [label]="t.field.label" [description]="t.field.help ?? ''"
                                   [required]="t.field.required" [invalid]="showErrors(t.field)"
                                   [disabled]="disabled || t.field.readOnly"
                                   [ngModel]="isOn(t)" (ngModelChange)="setToggle(t, $event)"
                                   [ngModelOptions]="{standalone: true}">
                    <ng-container *ngTemplateOutlet="groupFields; context: {group: group}"></ng-container>
                  </app-dl-checkbox>
                  @if (showErrors(t.field)) {
                    <p class="invalid-feedback d-block">{{ errors[t.field.path].join(' ') }}</p>
                  }
                </div>
              </div>
            } @else {
              <div class="col-12 col-lg-6" [attr.data-group]="group.name">
                <ng-container *ngTemplateOutlet="groupFields; context: {group: group}"></ng-container>
              </div>
            }
          }
        </div>
      </div>
    } @else {
      <ng-container *ngTemplateOutlet="groupBlock; context: {group: block.groups[0]}"></ng-container>
    }
  }
  }

  <!-- One standalone group under its h3. -->
  <ng-template #groupBlock let-group="group">
    <div role="group" class="cl-spacing-medium" [attr.data-group]="group.name" [attr.aria-labelledby]="groupId(group)">
      <h3 [id]="groupId(group)">{{ group.name || 'Template settings' }}</h3>
      <ng-container *ngTemplateOutlet="groupFields; context: {group: group}"></ng-container>
    </div>
  </ng-template>

  <ng-template #groupFields let-group="group">
    @if (hasPlain(group)) {
      <ng-container *ngTemplateOutlet="section; context: {group: group, advanced: false}"></ng-container>
    }
    <!-- The group's x-ui.advanced fields: always shown, after its other fields, in a part of their
         own labelled "Advanced" (user decision on #2761: no switch, no accordion). -->
    @if (hasGroupAdvanced(group)) {
      <!-- Set apart by its h4 and medium spacing, no ruler. -->
      <div role="group" class="cl-spacing-medium" data-advanced-fields [attr.aria-labelledby]="groupId(group) + '-advanced'">
        <h4 class="it-advanced-heading" [id]="groupId(group) + '-advanced'">Advanced</h4>
        <ng-container *ngTemplateOutlet="section; context: {group: group, advanced: true}"></ng-container>
      </div>
    }
  </ng-template>

  <!-- The fields of one group that belong to the plain or to the advanced part. A nested object is
       one fieldset, in the plain part when it has plain fields (its advanced ones in an "Advanced" part
       inside it, never a second fieldset of the same name), else in the advanced part. Its legend names a group,
       so no required asterisk. -->
  <ng-template #section let-group="group" let-advanced="advanced">
    <div class="row">
      @for (f of group.fields; track f.path) {
        @if (f.widget === 'object') {
          @if (advanced ? !childrenIn(f, false).length && childrenIn(f, true).length : childrenIn(f, false).length) {
            <div class="col-12" [attr.data-field]="f.path">
              <!-- No spacing class: measured on :4200 the column already puts 16 px above it, as above a label. -->
              <fieldset>
                @if (!toggles.has(group.name)) {
                  <legend class="it-object-legend">{{ f.label }}</legend>
                }
                <div class="row">
                  @for (c of childrenIn(f, advanced); track c.path) {
                    <ng-container *ngTemplateOutlet="field; context: {$implicit: c}"></ng-container>
                  }
                </div>
                @if (!advanced && childrenIn(f, true).length) {
                  <div role="group" class="cl-spacing-medium" data-advanced-fields [attr.aria-labelledby]="fieldId(f) + '-advanced'">
                    <h4 class="it-advanced-heading" [id]="fieldId(f) + '-advanced'">Advanced</h4>
                    <div class="row">
                      @for (c of childrenIn(f, true); track c.path) {
                        <ng-container *ngTemplateOutlet="field; context: {$implicit: c}"></ng-container>
                      }
                    </div>
                  </div>
                }
                @if (f.help && !advanced) {
                  <p class="valid-feedback">{{ f.help }}</p>
                }
              </fieldset>
            </div>
          }
        } @else if (f.advanced === advanced) {
          <ng-container *ngTemplateOutlet="field; context: {$implicit: f}"></ng-container>
        }
      }
    </div>
  </ng-template>
  @if (errors['']) {
    @for (e of errors['']; track e) {
      <p class="invalid-feedback d-block">{{ e }}</p>
    }
  }
}

<ng-template #field let-f>
  <!-- A select whose option texts are long spans the full width, so they are not cut off. -->
  <div [class]="fieldClass(f)"
       [attr.data-field]="f.path">
    @switch (f.widget) {
      @case ('ladder') {
        <app-encoding-ladder [formControl]="ladderControl(f)"></app-encoding-ladder>
      }
      @case ('switch') {
        <!-- cl-spacing-none: the column already carries the field spacing; castlabs-ui's 16 px above
             a form-check put a switch twice as far from a legend as a label (measured on :4200). -->
        <div class="form-check cl-spacing-none">
          <input class="form-check-input" type="checkbox" [id]="fieldId(f)" [disabled]="disabled || f.readOnly"
                 [class.invalid]="showErrors(f)"
                 [ngModel]="!!val(f.path)" (ngModelChange)="set(f.path, $event)"
                 [ngModelOptions]="{standalone: true}">
          <label class="form-check-label it-label" [for]="fieldId(f)">{{ f.label }}</label>
        </div>
      }
      @case ('multiselect') {
        @if (hasOptionHelp(f)) {
          <fieldset>
            <legend class="sr-only">{{ f.label }}</legend>
            @for (o of f.options; track $index) {
              <div [class.cl-spacing-small]="$index > 0">
                <app-dl-checkbox [checkboxId]="fieldId(f) + '-' + $index" [label]="o.label" [description]="o.help ?? ''"
                                 [invalid]="showErrors(f)" [disabled]="disabled || f.readOnly"
                                 [ngModel]="isSelected(f, o.value)" (ngModelChange)="toggleOption(f, o.value, $event)"
                                 [ngModelOptions]="{standalone: true}"></app-dl-checkbox>
              </div>
            }
          </fieldset>
        } @else {
          <fieldset class="cl-spacing-small">
            <legend>{{ f.label }}@if (f.required) {<span class="required">*</span>}</legend>
            <div class="d-flex flex-wrap align-items-center" data-options>
              @for (o of f.options; track $index) {
                <div class="form-check form-check-inline cl-spacing-none">
                  <input class="form-check-input" type="checkbox" [id]="fieldId(f) + '-' + $index"
                         [disabled]="disabled || f.readOnly" [checked]="isSelected(f, o.value)"
                         [class.invalid]="showErrors(f)" [attr.aria-invalid]="showErrors(f) || null"
                         (change)="toggleOption(f, o.value, $any($event.target).checked)">
                  <label class="form-check-label" [for]="fieldId(f) + '-' + $index">{{ o.label }}</label>
                </div>
              }
            </div>
          </fieldset>
        }
      }
      @default {
        <label class="form-label it-label" [for]="fieldId(f)">{{ f.label }}@if (f.required && !unmarked.has(f.path)) {<span class="required it-required">*</span>}</label>
        @switch (f.widget) {
          @case ('select') {
            <select class="form-select" [id]="fieldId(f)" [disabled]="disabled || f.readOnly"
                    [class.invalid]="showErrors(f)"
                    [ngModel]="val(f.path)" (ngModelChange)="set(f.path, $event)"
                    [ngModelOptions]="{standalone: true}">
              @for (o of selectOptions(f); track $index) {
                <option [ngValue]="o.value">{{ o.label }}</option>
              }
            </select>
          }
          @case ('textarea') {
            <textarea class="form-control" rows="4" [id]="fieldId(f)" [disabled]="disabled || f.readOnly"
                      [class.invalid]="showErrors(f)" [attr.maxlength]="f.maxLength"
                      [ngModel]="val(f.path)" (ngModelChange)="set(f.path, $event)"
                      [ngModelOptions]="{standalone: true}"></textarea>
          }
          @case ('tags') {
            <input class="form-control" type="text" [id]="fieldId(f)" [disabled]="disabled || f.readOnly"
                   [class.invalid]="showErrors(f)"
                   [ngModel]="tagsText(f)" (change)="setTags(f, $any($event.target).value)"
                   [ngModelOptions]="{standalone: true}">
          }
          @case ('number') {
            <input class="form-control" type="number" [id]="fieldId(f)" [disabled]="disabled || f.readOnly"
                   [class.invalid]="showErrors(f)" [attr.min]="f.minimum" [attr.max]="f.maximum"
                   [attr.step]="f.integer ? 1 : 'any'"
                   [ngModel]="val(f.path)" (ngModelChange)="set(f.path, $event)"
                   [ngModelOptions]="{standalone: true}">
          }
          @case ('day') {
            <!-- A date-time offered as a day (x-ui.day): shows the value's UTC day; picking one writes
                 its first or last second UTC, an untouched value stays as given. -->
            <input class="form-control" type="date" [id]="fieldId(f)" [disabled]="disabled || f.readOnly"
                   [class.invalid]="showErrors(f)"
                   [ngModel]="utcDayOf(val(f.path))" (ngModelChange)="set(f.path, dayToUtc($event, f.xui.day))"
                   [ngModelOptions]="{standalone: true}">
          }
          @default {
            <input class="form-control"
                   [type]="f.widget === 'date' ? 'date' : f.widget === 'datetime' ? 'datetime-local' : 'text'"
                   [id]="fieldId(f)" [disabled]="disabled || f.readOnly" [class.invalid]="showErrors(f)"
                   [attr.step]="f.widget === 'datetime' ? 1 : null"
                   [ngModel]="val(f.path)" (ngModelChange)="set(f.path, $event)"
                   [ngModelOptions]="{standalone: true}">
          }
        }
      }
    }
    <!-- No empty help placeholder (valid-feedback-none): it added an empty line under every field
         without help. castlabs-ui shows .invalid-feedback after an input.invalid by itself; a widget that wraps its
         input (switch, multiselect, ladder) needs it shown explicitly. One line: castlabs-ui only
         reaches the first siblings after the input. -->
    @if (showErrors(f)) {
      <p class="invalid-feedback it-error" [class.d-block]="!inputPrecedesFeedback(f)">{{ errors[f.path].join(' ') }}</p>
    } @else if (f.help) {
      <p class="valid-feedback">{{ f.help }}</p>
    }
  </div>
</ng-template>
